"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

const DIGITS = "0123456789";

/**
 * Rolls each digit of `value` up from zero the first time it scrolls into
 * view. Screen readers get the final figure once, from the visually hidden
 * text; the rolling strips are hidden from them.
 */
export function Odometer({
  value,
  decimals = 0,
  prefix = "",
  suffix = "",
  className,
}: {
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(false);
  const text = value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, []);

  let digitIndex = 0;
  return (
    <span ref={ref} className={cn("inline-flex tabular-nums", className)}>
      <span className="sr-only">{`${prefix}${text}${suffix}`}</span>
      <span aria-hidden="true" className="inline-flex">
        {prefix}
        {[...text].map((ch, i, all) => {
          const place = `p${all.length - i}`;
          if (!DIGITS.includes(ch)) {
            return <span key={place}>{ch}</span>;
          }
          const delay = digitIndex++ * 90;
          return (
            <span
              key={place}
              className="relative inline-block h-[1.1em] overflow-hidden leading-[1.1]"
            >
              <span
                className="flex flex-col transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                style={{
                  transform: `translateY(-${shown ? Number(ch) * 1.1 : 0}em)`,
                  transitionDelay: `${delay}ms`,
                }}
              >
                {[...DIGITS].map((d) => (
                  <span key={d} className="h-[1.1em]">
                    {d}
                  </span>
                ))}
              </span>
            </span>
          );
        })}
        {suffix}
      </span>
    </span>
  );
}
